const DEFAULT_MAX_MONTHS = 600;
const DEFAULT_ROUNDING_MODE = 'pennies_end';
const DEFAULT_INTEREST_METHOD = 'monthly_compound';

const roundToPennies = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const isDefined = (value) => value !== undefined && value !== null && value !== '';

const parseNumber = (name, value, { min = 0, max = null, required = true, integer = false } = {}) => {
  if (!isDefined(value)) {
    if (required) {
      throw new Error(`${name} is required.`);
    }
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    throw new Error(`${name} must be a finite number.`);
  }
  if (numeric < min) {
    throw new Error(`${name} must be >= ${min}.`);
  }
  if (max !== null && numeric > max) {
    throw new Error(`${name} must be <= ${max}.`);
  }
  if (integer && !Number.isInteger(numeric)) {
    throw new Error(`${name} must be an integer.`);
  }

  return numeric;
};

const parseEnum = (name, value, allowed, fallback) => {
  const resolved = isDefined(value) ? value : fallback;
  if (!allowed.includes(resolved)) {
    throw new Error(`${name} must be one of: ${allowed.join(', ')}.`);
  }
  return resolved;
};

const getMonthlyRate = (aprPercent) => Math.pow(1 + aprPercent / 100, 1 / 12) - 1;

const normalizeInputs = (inputs) => {
  if (!inputs || typeof inputs !== 'object') {
    throw new Error('inputs must be an object.');
  }

  const startingBalanceGBP = parseNumber('startingBalanceGBP', inputs.startingBalanceGBP, { min: 0 });
  const aprPercent = parseNumber('aprPercent', inputs.aprPercent, { min: 0, max: 100 });
  const monthlyPaymentGBP = parseNumber('monthlyPaymentGBP', inputs.monthlyPaymentGBP, { min: 0 });

  const minPaymentMode = parseEnum(
    'minPaymentMode',
    inputs.minPaymentMode,
    ['fixed', 'percent', 'greaterOf'],
    'fixed'
  );

  const minPaymentFixedGBP =
    minPaymentMode === 'fixed' || minPaymentMode === 'greaterOf'
      ? parseNumber('minPaymentFixedGBP', inputs.minPaymentFixedGBP, { min: 0 })
      : null;

  const minPaymentPercent =
    minPaymentMode === 'percent' || minPaymentMode === 'greaterOf'
      ? parseNumber('minPaymentPercent', inputs.minPaymentPercent, { min: 0, max: 100 })
      : null;

  const paymentStrategy = parseEnum(
    'paymentStrategy',
    inputs.paymentStrategy,
    ['pay_fixed', 'pay_minimum', 'pay_minimum_plus_extra'],
    'pay_fixed'
  );

  const extraPaymentGBP = parseNumber('extraPaymentGBP', inputs.extraPaymentGBP, { min: 0, required: false }) ?? 0;
  const monthlyNewSpendingGBP =
    parseNumber('monthlyNewSpendingGBP', inputs.monthlyNewSpendingGBP, { min: 0, required: false }) ?? 0;
  const monthlyFeesGBP = parseNumber('monthlyFeesGBP', inputs.monthlyFeesGBP, { min: 0, required: false }) ?? 0;

  const promoAprPercent = parseNumber('promoAprPercent', inputs.promoAprPercent, { min: 0, max: 100, required: false });
  const promoMonths = parseNumber('promoMonths', inputs.promoMonths, { min: 0, required: false, integer: true });

  if ((promoAprPercent !== null && promoMonths === null) || (promoMonths !== null && promoAprPercent === null)) {
    throw new Error('promoAprPercent and promoMonths must be provided together.');
  }

  const maxMonths =
    parseNumber('maxMonths', inputs.maxMonths, { min: 1, required: false, integer: true }) ?? DEFAULT_MAX_MONTHS;

  const roundingMode = parseEnum(
    'roundingMode',
    inputs.roundingMode,
    ['pennies_end', 'pennies_monthly'],
    DEFAULT_ROUNDING_MODE
  );

  const interestMethod = parseEnum(
    'interestMethod',
    inputs.interestMethod,
    ['monthly_compound'],
    DEFAULT_INTEREST_METHOD
  );

  return {
    startingBalanceGBP,
    aprPercent,
    monthlyPaymentGBP,
    minPaymentMode,
    minPaymentFixedGBP,
    minPaymentPercent,
    paymentStrategy,
    extraPaymentGBP,
    monthlyNewSpendingGBP,
    monthlyFeesGBP,
    promoAprPercent,
    promoMonths,
    maxMonths,
    roundingMode,
    interestMethod,
  };
};

const computeMinimumPayment = (balance, mode, fixed, percent) => {
  if (balance <= 0) return 0;
  if (mode === 'fixed') return fixed;
  const percentAmount = balance * (percent / 100);
  if (mode === 'percent') return percentAmount;
  return Math.max(fixed, percentAmount);
};

const formatValue = (value, roundingMode) => {
  if (roundingMode === 'pennies_monthly') {
    return roundToPennies(value);
  }
  return roundToPennies(value);
};

export const simulateCreditCardDebt = (inputs) => {
  const options = normalizeInputs(inputs);

  if (options.interestMethod !== 'monthly_compound') {
    throw new Error(`interestMethod ${options.interestMethod} is not supported.`);
  }

  const {
    startingBalanceGBP,
    aprPercent,
    monthlyPaymentGBP,
    minPaymentMode,
    minPaymentFixedGBP,
    minPaymentPercent,
    paymentStrategy,
    extraPaymentGBP,
    monthlyNewSpendingGBP,
    monthlyFeesGBP,
    promoAprPercent,
    promoMonths,
    maxMonths,
    roundingMode,
  } = options;

  let balance = startingBalanceGBP;
  let totalInterestPaid = 0;
  let totalFeesPaid = 0;
  let totalPaid = 0;
  const schedule = [];

  if (balance <= 0.005) {
    return {
      monthsToClear: 0,
      totalInterestPaid: 0,
      totalFeesPaid: 0,
      totalPaid: 0,
      finalBalance: 0,
      schedule,
    };
  }

  for (let monthIndex = 1; monthIndex <= maxMonths; monthIndex += 1) {
    const startingBalance = balance;

    const purchasesAdded = monthlyNewSpendingGBP;
    balance += purchasesAdded;

    const feesAdded = monthlyFeesGBP;
    balance += feesAdded;
    totalFeesPaid += feesAdded;

    if (roundingMode === 'pennies_monthly') {
      balance = roundToPennies(balance);
    }

    const activeApr = promoMonths && monthIndex <= promoMonths ? promoAprPercent : aprPercent;
    const monthlyRate = getMonthlyRate(activeApr);

    let interestCharged = balance * monthlyRate;
    if (roundingMode === 'pennies_monthly') {
      interestCharged = roundToPennies(interestCharged);
    }
    balance += interestCharged;
    if (roundingMode === 'pennies_monthly') {
      balance = roundToPennies(balance);
    }
    totalInterestPaid += interestCharged;

    let minimumPayment = computeMinimumPayment(balance, minPaymentMode, minPaymentFixedGBP, minPaymentPercent);
    minimumPayment = Math.min(balance, Math.max(0, minimumPayment));
    if (roundingMode === 'pennies_monthly') {
      minimumPayment = roundToPennies(minimumPayment);
    }

    let paymentCandidate = 0;
    if (balance <= 1) {
      paymentCandidate = balance;
    } else if (paymentStrategy === 'pay_fixed') {
      paymentCandidate = monthlyPaymentGBP;
    } else if (paymentStrategy === 'pay_minimum') {
      paymentCandidate = minimumPayment;
    } else {
      paymentCandidate = minimumPayment + extraPaymentGBP;
    }

    let paymentMade = Math.min(balance, Math.max(0, paymentCandidate));
    if (roundingMode === 'pennies_monthly') {
      paymentMade = roundToPennies(paymentMade);
    }

    balance -= paymentMade;
    if (roundingMode === 'pennies_monthly') {
      balance = roundToPennies(balance);
    }

    totalPaid += paymentMade;

    schedule.push({
      monthIndex,
      startingBalance: formatValue(startingBalance, roundingMode),
      purchasesAdded: formatValue(purchasesAdded, roundingMode),
      feesAdded: formatValue(feesAdded, roundingMode),
      interestCharged: formatValue(interestCharged, roundingMode),
      minimumPayment: formatValue(minimumPayment, roundingMode),
      paymentMade: formatValue(paymentMade, roundingMode),
      endingBalance: formatValue(balance, roundingMode),
    });

    if (balance <= 0.005) {
      return {
        monthsToClear: monthIndex,
        totalInterestPaid: formatValue(totalInterestPaid, roundingMode),
        totalFeesPaid: formatValue(totalFeesPaid, roundingMode),
        totalPaid: formatValue(totalPaid, roundingMode),
        finalBalance: 0,
        schedule,
      };
    }
  }

  return {
    monthsToClear: null,
    totalInterestPaid: formatValue(totalInterestPaid, roundingMode),
    totalFeesPaid: formatValue(totalFeesPaid, roundingMode),
    totalPaid: formatValue(totalPaid, roundingMode),
    finalBalance: formatValue(balance, roundingMode),
    schedule,
  };
};

export const findFixedPaymentForTargetMonths = (inputs, targetMonths, { tolerance = 0.01, maxIterations = 60 } = {}) => {
  const months = parseNumber('targetMonths', targetMonths, { min: 1, integer: true });
  const baseline = normalizeInputs({
    minPaymentMode: isDefined(inputs?.minPaymentMode) ? inputs.minPaymentMode : 'fixed',
    minPaymentFixedGBP: isDefined(inputs?.minPaymentFixedGBP) ? inputs.minPaymentFixedGBP : 0,
    minPaymentPercent: isDefined(inputs?.minPaymentPercent) ? inputs.minPaymentPercent : 0,
    ...inputs,
    paymentStrategy: 'pay_fixed',
  });

  let lower = 0;
  let upper = Math.max(1, baseline.startingBalanceGBP / months);
  const maxUpper = Math.max(baseline.startingBalanceGBP * 10, baseline.startingBalanceGBP + months * 1000);

  while (upper <= maxUpper) {
    const result = simulateCreditCardDebt({
      ...baseline,
      monthlyPaymentGBP: upper,
    });
    if (result.monthsToClear !== null && result.monthsToClear <= months) {
      break;
    }
    upper *= 2;
  }

  if (upper > maxUpper) {
    return null;
  }

  let best = upper;
  for (let i = 0; i < maxIterations; i += 1) {
    const mid = (lower + upper) / 2;
    const result = simulateCreditCardDebt({
      ...baseline,
      monthlyPaymentGBP: mid,
    });

    if (result.monthsToClear !== null && result.monthsToClear <= months) {
      best = mid;
      upper = mid;
    } else {
      lower = mid;
    }

    if (upper - lower <= tolerance) {
      break;
    }
  }

  return roundToPennies(best);
};

export const minimumPaymentTrapWarning = (result, thresholdMonths = 240) => {
  if (!result) return null;
  if (result.monthsToClear === null) {
    return 'Balance not cleared within the max months. Payments may be too low to reduce the debt.';
  }
  if (result.monthsToClear >= thresholdMonths) {
    return `Balance takes ${result.monthsToClear} months to clear. This is a long repayment horizon.`;
  }
  return null;
};

export const creditCardDebtDemoUsage = () => {
  const demo = simulateCreditCardDebt({
    startingBalanceGBP: 2500,
    aprPercent: 24.9,
    monthlyPaymentGBP: 120,
    minPaymentMode: 'greaterOf',
    minPaymentFixedGBP: 25,
    minPaymentPercent: 3,
    paymentStrategy: 'pay_minimum_plus_extra',
    extraPaymentGBP: 20,
    monthlyNewSpendingGBP: 0,
    monthlyFeesGBP: 0,
    promoAprPercent: 0,
    promoMonths: 0,
    maxMonths: 600,
    roundingMode: 'pennies_end',
    interestMethod: 'monthly_compound',
  });

  return {
    first12Rows: demo.schedule.slice(0, 12),
    summary: {
      monthsToClear: demo.monthsToClear,
      totalInterestPaid: demo.totalInterestPaid,
      totalFeesPaid: demo.totalFeesPaid,
      totalPaid: demo.totalPaid,
      finalBalance: demo.finalBalance,
    },
  };
};

// Demo usage:
// const { first12Rows, summary } = creditCardDebtDemoUsage();
// console.table(first12Rows);
// console.log(summary);
